import { useEffect, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/RoutesTranslated';

import { useLocation, useNavigate, type Location, NavigateFunction } from 'react-router-dom';

import ReactCodeInput from 'react-code-input';

import { useScreenSize, ScreenSize } from '@/ui/hooks/useScreenSize';

import { Container, Row, Col, Button } from 'react-bootstrap';
import { translate } from '@/domain/utils/translate';
import '@/ui/containers/userOptions/UserOptionsContainer/style.css';

import { useAppSelector } from '@/ui/store/helperRedux';

import UserController from '@/controllers/UserController';

import BackClose from '@/ui/components/BackClose';
import Breadcrumbs from '@/ui/components/Breadcrumbs';
import TextAndLink from '@/ui/components/TextAndLink';
import IconList from '@/ui/components/IconList';


/** Componente From para cambio de contraseña
 * @component
 */
const PasswordCodeForm = (): JSX.Element => {
    /** Hook para generar una navegación dentro de la aplicación. */
    const navigate: NavigateFunction = useNavigate();

    /** Hook para trabajar con document location  */
    const location: Location = useLocation();

    /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido  */
    const { width, maxLargeWidth }: ScreenSize = useScreenSize();
    /** Variable que define si es entorno mobile o desktop */
    const isMobile: boolean = width < maxLargeWidth;

    /** Declaración inicial de los botones como deshabilitado    */
    const [stateButton, setStateButton] = useState<string>('btn-primary-icon-standard disabled');

    /** Declaración inicial de los mensajes de error como vacios    */
    const [messageError, setMessageError] = useState<string>('');

    /** Habilita un botón para avanzar a la siguiente fase */
    const enableButton = (): void => setStateButton('btn-primary-text-standard');
    /** Deshabilita un botón para avanzar a la siguiente fase */
    const disableButton = (): void => setStateButton('btn-primary-text-standard disabled');

    /** Estado Redux del usuario */
    const userLogued = useAppSelector((state) => state.userState);


    /** Controlador para usuario */
    const { validateAuthCode, getAuthCode } = UserController();

    const INVALIDATE_CODE = translate('session.invalidate_code');
    const LABEL_TEXT = translate('session.label_text');
    const LINK_LABEL_TEXT = translate('session.link_label_text');
    const RECOVERY_TITLE: string = translate('session.recovery_password_title');
    const VERIFY_TEXT: string = translate('session.verify_text');
    const EMAIL_TEXT: string = translate('session.send_code_text').replace('EMAIL', userLogued.user.email ?? '');
    const SENT_CODE: string = translate('session.sent_code');
    const SENT_CODE_ERROR: string = translate('session.sent_code_error');

    const [messageSentMessage, setMessageSentMessage] = useState<string>('');

    /** Hook para manejar el estado del código de recuperación */
    const [code, setCode] = useState<string>('');
    const [isLinkDisabled, setIsLinkDisabled] = useState<boolean>(false);
    const [inputKey, setInputKey] = useState<number>(0);

    /** Rutas URLs */
    const routesDirections = getRoutesDirections();

    const goHome = (): void => {
        navigate(routesDirections.MAIN_ROUTE, { state: { originURL: location.pathname } });
    };

    const handleCodeChange = (value: string) => {
        setCode(value);
        if (value.length === 4) {
            enableButton();
            setMessageError('');
        } else {
            disableButton();
            setMessageError('');
        }
    };

    /** Solicita código de autorización para cambio de contraseña */
    const handleGetCode = async (): Promise<void> => {
        try {
            setIsLinkDisabled(true);
            setCode(''); // Limpiar el input del código
            setInputKey(prev => prev + 1); // Forzar actualización del input
            disableButton(); // Deshabilitar el botón de continuar
            const result = await getAuthCode(userLogued.user.email ?? '');
            if (result) {
                // Limpiar mensaje de error y mostrar mensaje de éxito
                setMessageError('');
                setMessageSentMessage(SENT_CODE);

                setTimeout(() => {
                    setMessageSentMessage('');
                    setIsLinkDisabled(false);
                }, 5000);
            } else {
                setMessageError(SENT_CODE_ERROR);
                setMessageSentMessage('');
                setIsLinkDisabled(false);
            }
        } catch (error) {
            setMessageError(SENT_CODE_ERROR);
            setMessageSentMessage('');
            setIsLinkDisabled(false);
        }
    };

    /** Validación del código de autoriazción */
    const handleValidateCode = async () => {
        disableButton();

        try {
            const validateCode = await validateAuthCode(userLogued.user.email ?? '', code);
            if (validateCode) {
                navigate(routesDirections.PASSWORD_RESET_ROUTE);
            } else {
                setMessageError(INVALIDATE_CODE);
                setMessageSentMessage(''); // Limpiar mensaje de éxito si hay error
            }
        } catch (error) {
            console.error(error);
        } if (!Error) {
            enableButton();
        }
    };

    //UseEffect para guardar el código de recuperación en el local storage
    useEffect(() => {
        localStorage.setItem('recoveryCode', code);

    }, [code]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (

        <Container fluid className='p-0 g-0'>
            <Row className='g-0'>
                {isMobile && (
                    <BackClose classMain='justify-content-between py-2'>
                        <Breadcrumbs />

                    </BackClose>
                )}
                <Col lg={12} className=' d-flex justify-content-center'>
                    <Col lg={7} xs={11} >

                        <Col className={isMobile ? 'd-flex flex-column pt-4' : ' d-flex flex-column mt-5 pt-5 '}>
                            <IconList
                                iconName='ico-key'
                                title={RECOVERY_TITLE}
                                classTitle='text-secondary-1 h3'
                                classIcon='svg-primary-3 pe-2' />
                            <span className='mt-3 text-secondary-1 paragraph'>{EMAIL_TEXT}</span>
                        </Col>

                        <ReactCodeInput
                            key={`code-${inputKey}`}
                            className='d-flex justify-content-center mt-5 pt-4 pb-4'
                            name='recoveryCode'
                            inputMode='numeric'
                            type='number'
                            fields={4}
                            value={code}
                            onChange={handleCodeChange}
                            inputStyle={{
                                margin: '0 5px',
                                width: '48px',
                                height: '54px',
                                textAlign: 'center',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                transition: 'border-color 0.3s',
                                outline: 'none',
                            }}
                        />
                        <TextAndLink
                            text={LABEL_TEXT}
                            linkText={LINK_LABEL_TEXT}
                            onClick={handleGetCode}
                            classText='text-center p-regular'
                            separated={false}
                            classLinkButton={`link-text-standard text-tertiary-4 p-regular fw-bold px-3 ${isLinkDisabled ? 'disabled' : ''}`}
                            disabled={isLinkDisabled}
                        />

                        {messageError ? (
                            <span className={isMobile ? 'text-danger p-regular px-4' : 'text-danger p-regular'}>{messageError}</span>
                        ) : messageSentMessage ? (
                            <span className={isMobile ? 'text-success p-regular px-4 ' : 'text-success p-regular '}>{messageSentMessage}</span>
                        ) : (
                            <Col className='pb-4' />
                        )}

                        <Button className={`${isMobile ? 'w-100 mt-4 mb-3' : 'w-100 mb-5 py-3'}  ${stateButton}`} type='button' onClick={handleValidateCode}>
                            <span className='p-large text-primary-4 ' >{VERIFY_TEXT}</span>
                        </Button>

                    </Col>
                </Col>
            </Row>


        </Container >

    );
};

export default PasswordCodeForm;
